import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Compass,
  Flame,
  Hammer,
  Lock,
  Mountain,
  Rocket,
  Swords,
  Target
} from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
import { STAGES_PER_LEVEL, questionsPerStage } from '../constants/gameplay';
import { API_URL } from '../lib/api';

const levelIcons = {
  flame: Flame,
  target: Target,
  mountain: Mountain,
  hammer: Hammer,
  rocket: Rocket
};

const levelNames = {
  en: { 1: 'Determination', 2: 'Discipline', 3: 'Perseverance', 4: 'Hard-working', 5: 'Breakthrough' },
  zh: { 1: '\u51b3\u5fc3', 2: '\u81ea\u5f8b', 3: '\u6bc5\u529b', 4: '\u52e4\u52b3', 5: '\u7a81\u7834' }
};

const stageRuleText = (language, levelNum) => {
  const required = questionsPerStage(levelNum);
  const total = required * STAGES_PER_LEVEL;
  if (language === 'zh') {
    return `每阶段连续答对 ${required} 题，完成 ${STAGES_PER_LEVEL} 个阶段共 ${total} 题后解锁下一关。`;
  }
  return `Clear ${STAGES_PER_LEVEL} stages with ${required} correct answers each, ${total} questions total, to unlock the next level.`;
};

const monsterModes = {
  level: {
    icon: BookOpen,
    color: '#8B5CF6',
    title: { en: 'Level Monster', zh: '\u5173\u5361\u602a\u517d' },
    rank: { en: 'Apprentice', zh: '\u5b66\u5f92' },
    subtitle: { en: 'Clear each stage perfectly to unlock the next level.', zh: '\u6bcf\u4e2a\u9636\u6bb5\u90fd\u9700\u5168\u90e8\u7b54\u5bf9\u624d\u80fd\u89e3\u9501\u4e0b\u4e00\u5173\u3002' }
  },
  battle: {
    icon: Swords,
    color: '#EF4444',
    title: { en: 'Battle Monster', zh: '\u7ade\u6280\u602a\u517d' },
    rank: { en: 'Master', zh: '\u5e08\u5085' },
    subtitle: { en: 'Win filtered stages without repeats from previous stages.', zh: '\u5b8c\u6210\u7b5b\u9009\u9898\u76ee\uff0c\u4e0d\u91cd\u590d\u4e0a\u4e00\u9636\u6bb5\u9898\u76ee\u3002' }
  },
  exploration: {
    icon: Compass,
    color: '#0EA5E9',
    title: { en: 'Exploration Monster', zh: '\u63a2\u7d22\u602a\u517d' },
    rank: { en: 'Legend', zh: '\u4f20\u5947' },
    subtitle: { en: 'Explore filtered questions across 3 perfect stages.', zh: '\u5728 3 \u4e2a\u5fc5\u987b\u5168\u5bf9\u7684\u9636\u6bb5\u4e2d\u63a2\u7d22\u7b5b\u9009\u9898\u76ee\u3002' }
  }
};

function Levels() {
  const [levels, setLevels] = useState([]);
  const [filters, setFilters] = useState({ subjects: [], forms: [], chapters: [], rows: [] });
  const [selectedFilters, setSelectedFilters] = useState({ subject_id: '', form_name: '', chapter: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const modeKey = new URLSearchParams(location.search).get('monster') || 'level';
  const mode = monsterModes[modeKey] || monsterModes.level;
  const MonsterIcon = mode.icon;

  const filterSearch = () => {
    const params = new URLSearchParams();
    params.set('monster', modeKey);
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return `?${params.toString()}`;
  };

  const availableForms = filters.forms.filter((formName) => (
    !selectedFilters.subject_id ||
    filters.rows.some((row) => row.subject_id === selectedFilters.subject_id && row.form_name === formName)
  ));
  const availableChapters = filters.chapters.filter((chapter) => (
    filters.rows.some((row) => (
      row.chapter === chapter &&
      (!selectedFilters.subject_id || row.subject_id === selectedFilters.subject_id) &&
      (!selectedFilters.form_name || row.form_name === selectedFilters.form_name)
    ))
  ));

  useEffect(() => {
    const fetchLevels = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [levelsRes, filtersRes] = await Promise.all([
          axios.get(`${API_URL}/api/levels`, { headers }),
          axios.get(`${API_URL}/api/question-filters`, { headers })
        ]);
        setLevels(levelsRes.data);
        setFilters(filtersRes.data || { subjects: [], forms: [], chapters: [], rows: [] });
      } catch (error) {
        toast.error(language === 'zh' ? '\u65e0\u6cd5\u52a0\u8f7d\u5173\u5361' : 'Failed to load levels');
      } finally {
        setLoading(false);
      }
    };

    fetchLevels();
  }, [language, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSelectedFilters({
      subject_id: params.get('subject_id') || '',
      form_name: params.get('form_name') || '',
      chapter: params.get('chapter') || ''
    });
  }, [location.search]);

  const updateFilter = (key, value) => {
    setSelectedFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === 'subject_id') {
        const formStillValid = !next.form_name || filters.rows.some((row) => row.subject_id === value && row.form_name === next.form_name);
        if (!formStillValid) next.form_name = '';
        const chapterStillValid = !next.chapter || filters.rows.some((row) => (
          row.subject_id === value &&
          (!next.form_name || row.form_name === next.form_name) &&
          row.chapter === next.chapter
        ));
        if (!chapterStillValid) next.chapter = '';
      }
      if (key === 'form_name') {
        const chapterStillValid = !next.chapter || filters.rows.some((row) => (
          (!next.subject_id || row.subject_id === next.subject_id) &&
          row.form_name === value &&
          row.chapter === next.chapter
        ));
        if (!chapterStillValid) next.chapter = '';
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-50 to-pink-50">
        <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="levels-page">
      <header className="border-b-2 border-zinc-200 bg-white/80 px-4 py-6 backdrop-blur-md">
        <div className="mx-auto max-w-7xl">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-4 flex items-center gap-2 font-medium text-zinc-600 hover:text-zinc-900"
            data-testid="levels-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            {t('back')}
          </button>

          <div className="flex items-center gap-4 rounded-2xl border-2 border-zinc-200 bg-white p-4">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${mode.color}18` }}
            >
              <MonsterIcon className="h-7 w-7" style={{ color: mode.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-black uppercase text-zinc-400">{t('levels')}</p>
              <h1 className="text-2xl font-black text-zinc-900">{mode.title[language]}</h1>
              <p className="mt-1 text-sm font-black text-zinc-700">
                {language === 'zh' ? '\u4fee\u884c\u8005' : 'Cultivator'}:
                {' '}
                <span style={{ color: mode.color }}>{mode.rank[language]}</span>
              </p>
              <p className="mt-1 text-sm font-medium text-zinc-500">{mode.subtitle[language]}</p>
              <p className="mt-1 text-sm font-black text-zinc-600">
                {language === 'zh'
                  ? '\u6bcf\u4e00\u5173\u90fd\u6709\u72ec\u7acb\u7684\u9636\u6bb5\u9898\u6570\u8981\u6c42\u3002'
                  : 'Each level has its own required question count per stage.'}
              </p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 rounded-2xl border-2 border-zinc-200 bg-white p-4 md:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-xs font-black uppercase text-zinc-400">
                {language === 'zh' ? '\u79d1\u76ee' : 'Subject'}
              </span>
              <select
                value={selectedFilters.subject_id}
                onChange={(event) => updateFilter('subject_id', event.target.value)}
                className="w-full rounded-xl border-2 border-zinc-200 bg-white px-3 py-2 text-sm font-bold text-zinc-700 focus:border-violet-500 focus:outline-none"
                data-testid="level-filter-subject"
              >
                <option value="">{language === 'zh' ? '\u5168\u90e8\u79d1\u76ee' : 'All Subjects'}</option>
                {filters.subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {language === 'zh' ? subject.name_zh : subject.name_en}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-black uppercase text-zinc-400">
                {language === 'zh' ? '\u5e74\u7ea7' : 'Form'}
              </span>
              <select
                value={selectedFilters.form_name}
                onChange={(event) => updateFilter('form_name', event.target.value)}
                className="w-full rounded-xl border-2 border-zinc-200 bg-white px-3 py-2 text-sm font-bold text-zinc-700 focus:border-violet-500 focus:outline-none"
                data-testid="level-filter-form"
              >
                <option value="">{language === 'zh' ? '\u5168\u90e8\u5e74\u7ea7' : 'All Forms'}</option>
                {availableForms.map((formName) => (
                  <option key={formName} value={formName}>{formName}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-black uppercase text-zinc-400">
                {language === 'zh' ? '\u7ae0\u8282' : 'Chapter'}
              </span>
              <select
                value={selectedFilters.chapter}
                onChange={(event) => updateFilter('chapter', event.target.value)}
                className="w-full rounded-xl border-2 border-zinc-200 bg-white px-3 py-2 text-sm font-bold text-zinc-700 focus:border-violet-500 focus:outline-none"
                data-testid="level-filter-chapter"
              >
                <option value="">{language === 'zh' ? '\u5168\u90e8\u7ae0\u8282' : 'All Chapters'}</option>
                {availableChapters.map((chapter) => (
                  <option key={chapter} value={chapter}>{chapter}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {levels.map((level, index) => {
            const Icon = levelIcons[level.icon] || Flame;
            const isLocked = !level.is_unlocked;

            return (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => !isLocked && navigate(`/level/${level.level_num}${filterSearch()}`)}
                className={`rounded-2xl border-2 bg-white p-6 transition-all ${
                  isLocked
                    ? 'cursor-not-allowed border-zinc-200 opacity-60'
                    : 'group cursor-pointer border-zinc-200 hover:border-violet-300 hover:shadow-md'
                }`}
                data-testid={`level-card-${level.level_num}`}
              >
                <div className="mb-4 flex items-start justify-between">
                  <div
                    className={`rounded-xl p-3 ${isLocked ? 'bg-zinc-100' : ''}`}
                    style={{ backgroundColor: isLocked ? undefined : `${level.color}20` }}
                  >
                    {isLocked ? (
                      <Lock className="h-8 w-8 text-zinc-400" />
                    ) : (
                      <Icon className="h-8 w-8" style={{ color: level.color }} />
                    )}
                  </div>
                  {!isLocked && <ChevronRight className="h-5 w-5 text-zinc-400 transition-colors group-hover:text-violet-500" />}
                </div>

                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-bold text-zinc-400">{t('level')} {level.level_num}</span>
                </div>
                <h3 className="mb-2 text-xl font-black text-zinc-900">
                  {levelNames[language]?.[level.level_num] || level.title || `Level ${level.level_num}`}
                </h3>

                {isLocked ? (
                  <p className="text-sm text-zinc-500">
                    {language === 'zh'
                      ? `完成上一关的 ${STAGES_PER_LEVEL} 个阶段后解锁`
                      : `Unlocks after clearing all ${STAGES_PER_LEVEL} stages in the previous level`}
                  </p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">{level.stages_completed || 0}/{STAGES_PER_LEVEL} {t('stages')}</span>
                      <span className="font-bold" style={{ color: level.color }}>
                        {(((level.stages_completed || 0) / STAGES_PER_LEVEL) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${((level.stages_completed || 0) / STAGES_PER_LEVEL) * 100}%`,
                          backgroundColor: level.color
                        }}
                      />
                    </div>
                    <p className="text-xs font-bold text-zinc-500">
                      {stageRuleText(language, level.level_num)}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default Levels;
